import React, { useState, useEffect } from 'react';
import { Alert, Button, Card, Form, Spinner } from 'react-bootstrap';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  registerPasskey,
  authenticateWithPasskey,
} from '../utils/passkeys';
import styles from './LoginPage.module.css';
import { apiClient, sessionManager } from '../api';
import { ROUTES } from '../routes';

interface LoginPageProps {
  onLogin: () => void;
}

const LoginShell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className={`d-flex align-items-center justify-content-center p-3 ${styles.loginPage}`}>
    <Card className={`p-4 ${styles.loginCard}`}>{children}</Card>
  </div>
);

const LoginLogo: React.FC<{ subtitle?: string }> = ({ subtitle }) => (
  <div className="text-center mb-4">
    <img src="/dashboard/aff_logo.svg" alt="Affinidi Logo" style={{ maxWidth: '200px' }} />
    <h1 className="h5 fw-bold text-primary mt-3 mb-1">
      Affinidi Trust Fabric
      <br />
      Trust Gateway
    </h1>
    {subtitle && <h2 className="h6 text-secondary mb-0">{subtitle}</h2>}
  </div>
);

const LoginPage: React.FC<LoginPageProps> = ({ onLogin }) => {
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [username, setUsername] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [supported, setSupported] = useState(false);
  const [platformAuthAvailable, setPlatformAuthAvailable] = useState(false);
  const [authMode, setAuthMode] = useState<'passkey' | 'saml' | null>(null);
  const [initializing, setInitializing] = useState(true);

  useEffect(() => {
    const checkAuthAndMode = async () => {
      if (sessionManager.getSessionToken()) {
        try {
          const authCheck = await apiClient.fetch('/api/auth/check');
          if (authCheck.ok) {
            await authCheck.json();
            window.location.href = ROUTES.DASHBOARD;
            return;
          }
        } catch {
          // Not authenticated, continue to login
        }
      }

      try {
        const response = await apiClient.fetch('/api/v1/auth/mode');
        const data = await response.json();
        setAuthMode(data.mode);
        if (data.mode === 'passkey') {
          setSupported(isWebAuthnSupported());
          isPlatformAuthenticatorAvailable().then(setPlatformAuthAvailable);
        }
      } catch (err) {
        console.error('Failed to fetch auth mode:', err);
        setAuthMode('passkey');
        setSupported(isWebAuthnSupported());
        isPlatformAuthenticatorAvailable().then(setPlatformAuthAvailable);
      } finally {
        setInitializing(false);
      }
    };

    checkAuthAndMode();

    const savedUsername = sessionManager.getCookie('lastUsername');
    if (savedUsername) setUsername(savedUsername);
  }, []);

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      await registerPasskey(username);
      setSuccess('Passkey registered successfully! You can now log in.');
      setMode('login');
      setUsername('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    if (authMode === 'saml') {
      await new Promise(resolve => setTimeout(resolve, 100));
      window.location.href = '/api/saml/login';
      return;
    }

    try {
      await authenticateWithPasskey(username);
      onLogin();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoading(false);
    }
  };

  if (authMode === null || initializing) {
    return (
      <LoginShell>
        <LoginLogo />
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      </LoginShell>
    );
  }

  if (authMode === 'saml') {
    return (
      <LoginShell>
        <LoginLogo subtitle="Sign In" />
        {error && <Alert variant="danger">{error}</Alert>}
        <Form onSubmit={handleLogin}>
          <div className="d-grid">
            <Button
              variant="primary"
              type="submit"
              disabled={loading}
              className={`d-flex align-items-center justify-content-center gap-2 ${styles.submitBtn}`}
            >
              {loading ? (
                <>
                  <Spinner animation="border" size="sm" />
                  Redirecting...
                </>
              ) : (
                <>
                  <i className="fas fa-sign-in-alt" />
                  Sign in with SSO
                </>
              )}
            </Button>
          </div>
        </Form>
      </LoginShell>
    );
  }

  if (!supported && authMode === 'passkey') {
    return (
      <LoginShell>
        <h2 className="h5 fw-bold mb-3">WebAuthn Not Supported</h2>
        <Alert variant="danger">
          Your browser doesn't support WebAuthn/Passkeys. Please use a modern browser like Chrome,
          Edge, Safari, or Firefox.
        </Alert>
      </LoginShell>
    );
  }

  return (
    <LoginShell>
      <LoginLogo subtitle={mode === 'login' ? 'Sign In' : 'Register'} />

      {error && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Form onSubmit={mode === 'login' ? handleLogin : handleRegister}>
        <Form.Group className="mb-3">
          <Form.Label htmlFor="username">Username</Form.Label>
          <Form.Control
            id="username"
            type="text"
            value={username}
            onChange={e => setUsername(e.target.value)}
            placeholder="Enter your username"
            required
            disabled={loading}
            autoComplete="username"
            autoFocus
          />
        </Form.Group>

        <div className="d-grid">
          <Button
            variant="primary"
            type="submit"
            disabled={loading || !username.trim()}
            className={`d-flex align-items-center justify-content-center gap-2 ${styles.submitBtn}`}
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" />
                {mode === 'login' ? 'Authenticating...' : 'Registering...'}
              </>
            ) : (
              <>
                <i className={`fas ${mode === 'login' ? 'fa-fingerprint' : 'fa-user-plus'}`} />
                {mode === 'login' ? 'Sign in with Passkey' : 'Register Passkey'}
              </>
            )}
          </Button>
        </div>
      </Form>

      <div className="text-center mt-4 text-muted small">
        {mode === 'login' ? (
          <p className="mb-0">
            Don't have a passkey?{' '}
            <button
              className="btn btn-link p-0 fw-semibold small"
              type="button"
              onClick={() => {
                setMode('register');
                setError(null);
                setSuccess(null);
              }}
              disabled={loading}
            >
              Register here
            </button>
          </p>
        ) : (
          <p className="mb-0">
            Already have a passkey?{' '}
            <button
              className="btn btn-link p-0 fw-semibold small"
              type="button"
              onClick={() => {
                setMode('login');
                setError(null);
                setSuccess(null);
              }}
              disabled={loading}
            >
              Sign in here
            </button>
          </p>
        )}
      </div>

      {platformAuthAvailable && (
        <div className="alert alert-success d-flex align-items-center gap-2 mt-3 mb-0 small">
          <i className="fas fa-shield-alt" />
          Biometric authentication available on this device
        </div>
      )}
    </LoginShell>
  );
};

export default LoginPage;
